/** @jsxImportSource @emotion/react */

import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { findLast } from 'lodash'
import { findAllQuestionPassageBank_findAllQuestionPassageBankCMS_items as QuestionPassageBank } from '__generatedTypes__/findAllQuestionPassageBank'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Table from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { Tooltip } from 'components/Tooltip'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import * as classes from '../styles'
import { durationToString } from '../../../PracticeTests/Form'
import { CARS_SPEED_READING_GAME } from '../../../QuestionPassages/Form/consts'

type Props = {
  gameSlug: string
  questionPassageBanks: QuestionPassageBank[]
  submittingQuestionPassageBankId?: string
  disableTableButtons?: boolean
  onIsLiveChange: (questionPassageBank: QuestionPassageBank, isLive: boolean) => void
  onAddClick: () => void
  alwaysDisplayLiveSwitch?: boolean
}

export const QuestionPassageBanksTable: FC<Props> = ({
  gameSlug,
  questionPassageBanks,
  submittingQuestionPassageBankId,
  disableTableButtons,
  onIsLiveChange,
  onAddClick,
  alwaysDisplayLiveSwitch = false,
}) => {
  const { getLink } = useLink()

  const lastLiveOrder = findLast(questionPassageBanks, ({ isLive }) => isLive)?.order ?? null

  return (
    <>
      <Table
        columns={[
          {
            title: 'Title',
            renderCell: (questionPassageBank) => questionPassageBank.title,
            flex: 1,
          },
          ...(gameSlug === CARS_SPEED_READING_GAME
            ? [
                {
                  title: 'WPM',
                  renderCell: (questionPassageBank: QuestionPassageBank) => String(questionPassageBank.wpm),
                  flex: 1,
                },
              ]
            : []),
          {
            title: 'Passage Time',
            renderCell: (questionPassageBank) => durationToString(questionPassageBank.passageTime),
            flex: 1,
          },
          {
            title: '# of CARS Questions',
            renderCell: (questionPassageBank) => String(questionPassageBank.questionPassagesIds.length),
            flex: '0 1 170px',
          },
          {
            title: 'Live',
            renderCell: (questionPassageBank) => {
              const canNotBeLive = !questionPassageBank.isLive && questionPassageBank.order !== (lastLiveOrder ?? 0) + 1
              const canNotBeNotLive = questionPassageBank.isLive

              if (!alwaysDisplayLiveSwitch && (canNotBeLive || canNotBeNotLive)) {
                return (
                  <Tooltip
                    title={
                      canNotBeLive
                        ? `You cannot enable Live for this CARS Question Bank as it may disrupt order for Game, please turn on Live for Level ${
                            (lastLiveOrder ?? 0) + 1
                          } first`
                        : `You cannot disable Live for this CARS Question Bank since it's already used for Game`
                    }
                    isNote
                    arrow
                  >
                    <Switch
                      checked={questionPassageBank.isLive}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, isLive) => onIsLiveChange(questionPassageBank, isLive)}
                      disabled
                    />
                  </Tooltip>
                )
              }

              return (
                <Switch
                  checked={questionPassageBank.isLive}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e, isLive) => onIsLiveChange(questionPassageBank, isLive)}
                  disabled={questionPassageBank.id === submittingQuestionPassageBankId}
                />
              )
            },
            flex: '0 1 50px',
          },
          {
            renderCell: (questionPassageBank) => (
              <Link to={getLink(cmsRoutes.CARS_QUESTION_BANK_EDIT, { carsQuestionBankId: questionPassageBank.id })}>
                <IconButton size="small" css={classes.link}>
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={questionPassageBanks}
        isSortable={false}
        wrapperMinWidth={720}
      />
      <TableButtons itemName="CARS Question Bank" onAddItem={onAddClick} disabled={disableTableButtons} />
    </>
  )
}
